/**
 * The in-app hatch pipeline: the specimen core (launchpad/specimen-core.mjs) and the
 * design's own specimen drawing run directly inside the web app - real thought
 * data from the indexer, real BANC annotations, the real naming/symbol/art
 * derivation, and the JEV profile client with its deterministic template
 * fallback. No CLI subprocess; no logic duplicated here.
 *
 * Nothing here talks to o1 any more: the hatch ends at a SpecimenCard mint,
 * which the app signs through the visitor's own wallet (see feed.ts).
 */

import { artSvg } from "./design-art";
import { buildGenome, bytesToBase64, fetchProfile } from "../../launchpad/specimen-core.mjs";

import type { Genome, Profile } from "../../launchpad/specimen-core.mjs";
import annBinUrl from "../../launchpad/data/neuron_annotations.bin?url";
import annDocUrl from "../../launchpad/data/neuron_annotations.json?url";

import type { Tick } from "./api";

type AnnotationTables = { readonly bin: Uint8Array; readonly doc: unknown };

let tables: AnnotationTables | null = null;

/** The verified BANC v888 annotation table, fetched once per session. */
export async function loadAnnotationTables(): Promise<AnnotationTables> {
  if (tables !== null) return tables;

  const [binResponse, docResponse] = await Promise.all([fetch(annBinUrl), fetch(annDocUrl)]);

  if (!binResponse.ok) throw new Error(`annotation table: HTTP ${binResponse.status}`);

  if (!docResponse.ok) throw new Error(`annotation dictionaries: HTTP ${docResponse.status}`);

  tables = { bin: new Uint8Array(await binResponse.arrayBuffer()), doc: await docResponse.json() };

  return tables;
}

/** The pipeline's thought input, read from the indexer's decoded BrainState. */
/**
 * The picked specimen's own launch material: what the visitor's press prepares from.
 * The thought carries its own feeder (D5), the one wallet that can mint the card.
 */
export type ThoughtInput = {
  readonly tx: string;
  readonly feeder: string;
  readonly tick: number;
  readonly stateRoot: string;
  readonly firedIds: readonly number[];
  readonly synapses: number;
  readonly spiked: number;
  readonly potentials: readonly string[];
};

export function thoughtFromTick(tick: Tick, feeder: string): ThoughtInput {
  return {
    tx: tick.txHash,
    feeder,
    tick: tick.tick,
    stateRoot: tick.stateRoot,
    firedIds: [...tick.fired],
    synapses: tick.synapses,
    spiked: tick.spiked,
    potentials: tick.readout.map(String),
  };
}

/** The real genome: naming, symbol and seed from the launchpad core. */
export async function deriveGenome(thought: ThoughtInput, neuronId: number): Promise<HatchGenome> {
  return buildGenome(thought, neuronId, await loadAnnotationTables());
}

/**
 * The biology a card carries on chain, packed exactly as SpecimenCard's header
 * documents: region, flow, side, the annotation table indices, what the thought
 * did, and the first 7 bytes of the specimen's own art seed.
 *
 * The indices come from the annotation table itself rather than from the names,
 * so the card's attributes point back at the verified BANC v888 file the whole
 * app is built on, and anyone can check them against it.
 */
export type TraitInput = {
  readonly neuronId: number;
  readonly seed: string;
  readonly synapses: number;
  readonly spiked: number;
  readonly firedCount: number;
};

export async function traitsFor(genome: TraitInput): Promise<bigint> {
  const { bin } = await loadAnnotationTables();
  const off = genome.neuronId * 5;

  if (off + 5 > bin.length)
    throw new Error(`neuron ${genome.neuronId} is outside the annotation table`);

  const superClass = BigInt(bin[off] ?? 0);
  const cellClass = BigInt(bin[off + 1] ?? 0);
  const cellType = BigInt(((bin[off + 2] ?? 0) << 8) | (bin[off + 3] ?? 0));
  const flags = bin[off + 4] ?? 0;
  const region = BigInt(flags & 3);
  const flow = BigInt((flags >> 3) & 3);
  const side = BigInt((flags >> 2) & 1);
  // the seed prefix is the art's own fingerprint: the same thought keeps the same art
  const seedPrefix = BigInt(`0x${genome.seed.replace(/^0x/, "").slice(0, 14)}`);
  const synapses = BigInt(Math.min(genome.synapses, 1023));
  const spiked = BigInt(Math.min(genome.spiked, 1023));
  const fired = BigInt(Math.min(genome.firedCount, 255));

  return (
    region |
    (flow << 2n) |
    (side << 4n) |
    (superClass << 8n) |
    (cellClass << 16n) |
    (cellType << 24n) |
    (synapses << 40n) |
    (spiked << 50n) |
    (fired << 60n) |
    (seedPrefix << 68n)
  );
}

/** The plate's own ground: the drawing is light ink, so it needs a dark field. */
const ART_GROUND = "oklch(14.5% 0.024 215)";

/** How big the rendered specimen image is: square, and large enough for a card face. */
const ART_SIZE = 512;

/**
 * The specimen's drawing as a decoded image, ready for a canvas.
 *
 * The token's image and every plate on the page come through here, so a visitor
 * reviews exactly the picture the launch will carry.
 */
async function artImage(seed: string, variant: number): Promise<HTMLImageElement> {
  const svg = artSvg(seed, ART_SIZE, variant);
  const image = new Image();

  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

  await image.decode();

  return image;
}

/**
 * Paints the specimen's own drawing into a canvas: this is the token's image,
 * at whatever plate size the design gives us.
 */
export async function paintArt(
  canvas: HTMLCanvasElement,
  seed: string,
  variant: number,
): Promise<void> {
  const ctx = canvas.getContext("2d");

  if (ctx === null) return;

  const image = await artImage(seed, variant);

  ctx.fillStyle = ART_GROUND;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
}

/** Which of the three drawings a specimen carries: stable, and the page's own. */
export function artVariant(neuronId: number): number {
  return neuronId % 3;
}

export type HatchGenome = Genome;

export type SpecimenProfile = Profile;

export { bytesToBase64, fetchProfile };
